import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  Image,
  TextInput,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Animated, { FadeInDown } from 'react-native-reanimated';
import AnimatedPressable from '@/components/AnimatedPressable';
import { noOutlineStyle, isWeb } from '@/lib/platform';
import {
  Bug,
  Lightbulb,
  Palette,
  HelpCircle,
  Smartphone,
  Globe,
  ArrowLeft,
  Send,
  User,
  Clock,
  ChevronDown,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import {
  useAdminRole,
  useAdminFeedbackDetail,
  useUpdateFeedbackStatus,
  useUpdateFeedbackPriority,
  useAddFeedbackNote,
  hasPermission,
} from '@/lib/admin';
import type { FeedbackStatus, FeedbackPriority } from '@/lib/admin';
import type { FeedbackCategory } from '@/lib/feedback/types';
import { useScreenshotUrl } from '@/lib/feedback/use-screenshot-url';
import { hapticLight } from '@/lib/haptics';
import { AppLayout } from '@/components/AppLayout';
import { WebCard } from '@/components/web/WebAppShell';
import { FeedbackStatusBadge } from '@/components/admin/FeedbackStatusBadge';
import { FeedbackPriorityBadge } from '@/components/admin/FeedbackPriorityBadge';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';

const CATEGORY_ICON: Record<FeedbackCategory, typeof Bug> = {
  bug: Bug,
  feature: Lightbulb,
  ux: Palette,
  other: HelpCircle,
};

const CATEGORY_COLOR: Record<FeedbackCategory, string> = {
  bug: '#FF3B30',
  feature: '#007AFF',
  ux: '#FF9500',
  other: '#8E8E93',
};

const STATUSES: FeedbackStatus[] = ['new', 'in_review', 'resolved', 'closed'];
const PRIORITIES: FeedbackPriority[] = ['low', 'medium', 'high', 'critical'];

export default function FeedbackDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const colors = useThemeColors();

  const { data: adminRole } = useAdminRole();
  const { data: detail, isLoading } = useAdminFeedbackDetail(id);
  // The screenshot bucket is private — resolve a short-lived signed URL. Called
  // unconditionally (before the isLoading early-return) to satisfy hook rules.
  const { data: screenshotUrl } = useScreenshotUrl(detail?.feedback.screenshot_path);
  const updateStatus = useUpdateFeedbackStatus();
  const updatePriority = useUpdateFeedbackPriority();
  const addNote = useAddFeedbackNote();

  const [noteText, setNoteText] = useState('');
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [showPriorityPicker, setShowPriorityPicker] = useState(false);

  const canManage = hasPermission(adminRole, 'feedback_manage');
  const canAddNotes = hasPermission(adminRole, 'feedback_notes');

  if (isLoading || !detail) {
    return (
      <AppLayout
        title="Feedback"
        breadcrumbs={[
          { label: 'Admin', href: '/(admin)/admin-dashboard' },
          { label: 'Feedback', href: '/(admin)/feedback' },
          { label: `#${id ?? ''}` },
        ]}
      >
        <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
          <ActivityIndicator size="large" color={colors.primary[600]} />
        </View>
      </AppLayout>
    );
  }

  const fb = detail.feedback;
  const Icon = CATEGORY_ICON[fb.category];
  const categoryColor = CATEGORY_COLOR[fb.category];

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleStatusChange = (status: FeedbackStatus) => {
    hapticLight();
    setShowStatusPicker(false);
    updateStatus.mutate({ feedbackId: fb.id, status });
  };

  const handlePriorityChange = (priority: FeedbackPriority) => {
    hapticLight();
    setShowPriorityPicker(false);
    updatePriority.mutate({ feedbackId: fb.id, priority });
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    hapticLight();
    addNote.mutate(
      { feedbackId: fb.id, text: noteText.trim() },
      { onSuccess: () => setNoteText('') }
    );
  };

  return (
    <AppLayout
      title="Feedback Detail"
      breadcrumbs={[
        { label: 'Admin', href: '/(admin)/admin-dashboard' },
        { label: 'Feedback', href: '/(admin)/feedback' },
        { label: `#${fb.id}` },
      ]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={isWeb ? [] : ['top', 'bottom']}>
        <KeyboardAwareBody className="flex-1" showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>
          {/* Back button (mobile) */}
          <AnimatedPressable
            onPress={() => router.back()}
            className="flex-row items-center gap-2 mb-4"
          >
            <ArrowLeft size={18} color={colors.primary[600]} />
            <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">
              Back to Feedback
            </Text>
          </AnimatedPressable>

          {/* Header card */}
          <Animated.View entering={FadeInDown.delay(100).duration(400)} className="mb-4">
            <WebCard>
              <View className="flex-row items-center gap-2 flex-wrap mb-3">
                <View
                  className="px-2.5 py-1 rounded-lg flex-row items-center gap-1.5"
                  style={{ backgroundColor: `${categoryColor}15` }}
                >
                  <Icon size={14} color={categoryColor} />
                  <Text style={{ color: categoryColor }} className="text-xs font-bold uppercase">
                    {fb.category}
                  </Text>
                </View>
                <FeedbackStatusBadge status={fb.status} size="md" />
                <FeedbackPriorityBadge priority={fb.priority} size="md" />
              </View>

              <View className="flex-row items-center gap-3">
                <View className="flex-row items-center gap-1">
                  <Clock size={12} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    Created: {formatDate(fb.created_at)}
                  </Text>
                </View>
                {fb.updated_at !== fb.created_at && (
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    Updated: {formatDate(fb.updated_at)}
                  </Text>
                )}
              </View>
            </WebCard>
          </Animated.View>

          {/* Feedback content */}
          <Animated.View entering={FadeInDown.delay(150).duration(400)} className="mb-4">
            <WebCard>
              <Text style={{ color: colors.text.primary }} className="text-sm leading-relaxed">
                {fb.text}
              </Text>
              {fb.screenshot_path && screenshotUrl && (
                <Image
                  source={{ uri: screenshotUrl }}
                  className="w-full h-56 rounded-xl mt-4"
                  resizeMode="cover"
                />
              )}
            </WebCard>
          </Animated.View>

          {/* Meta info */}
          <Animated.View entering={FadeInDown.delay(200).duration(400)} className="mb-4">
            <WebCard>
              <Text style={{ color: colors.text.primary }} className="text-sm font-bold mb-3">
                Details
              </Text>
              <View className="gap-2.5">
                <MetaRow label="User" value={fb.user_email} colors={colors} onPress={() => {
                  hapticLight();
                  router.push(`/(admin)/user-detail?id=${fb.user_id}`);
                }} />
                <MetaRow label="Platform" value={fb.platform} colors={colors} icon={
                  fb.platform === 'web'
                    ? <Globe size={13} color={colors.text.tertiary} />
                    : <Smartphone size={13} color={colors.text.tertiary} />
                } />
                {fb.screen_context && <MetaRow label="Screen" value={fb.screen_context} colors={colors} />}
                {fb.app_version && <MetaRow label="Version" value={fb.app_version} colors={colors} />}
                {fb.device_info?.os ? <MetaRow label="Device" value={String(fb.device_info.os)} colors={colors} /> : null}
                {fb.device_info?.model ? <MetaRow label="Model" value={String(fb.device_info.model)} colors={colors} /> : null}
              </View>
            </WebCard>
          </Animated.View>

          {/* Action bar (status + priority pickers) */}
          {canManage && (
            <Animated.View entering={FadeInDown.delay(250).duration(400)} className="mb-4">
              <WebCard>
                <Text style={{ color: colors.text.primary }} className="text-sm font-bold mb-3">
                  Actions
                </Text>
                <View className="flex-row gap-3">
                  {/* Status picker */}
                  <View className="flex-1">
                    <Text style={{ color: colors.text.secondary }} className="text-xs font-medium mb-1.5">
                      Status
                    </Text>
                    <AnimatedPressable
                      onPress={() => {
                        hapticLight();
                        setShowStatusPicker(!showStatusPicker);
                        setShowPriorityPicker(false);
                      }}
                      className="flex-row items-center justify-between px-3 py-2.5 rounded-xl"
                      style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
                    >
                      <FeedbackStatusBadge status={fb.status} size="md" />
                      <ChevronDown size={14} color={colors.text.tertiary} />
                    </AnimatedPressable>
                    {showStatusPicker && (
                      <View
                        className="rounded-xl mt-1 overflow-hidden"
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderWidth: 1,
                          borderColor: colors.border.light,
                        }}
                      >
                        {STATUSES.map((s) => (
                          <AnimatedPressable
                            key={s}
                            onPress={() => handleStatusChange(s)}
                            className="px-3 py-2.5"
                            style={s !== STATUSES[STATUSES.length - 1] ? {
                              borderBottomWidth: 1,
                              borderBottomColor: colors.border.light,
                            } : undefined}
                          >
                            <FeedbackStatusBadge status={s} size="md" />
                          </AnimatedPressable>
                        ))}
                      </View>
                    )}
                  </View>

                  {/* Priority picker */}
                  <View className="flex-1">
                    <Text style={{ color: colors.text.secondary }} className="text-xs font-medium mb-1.5">
                      Priority
                    </Text>
                    <AnimatedPressable
                      onPress={() => {
                        hapticLight();
                        setShowPriorityPicker(!showPriorityPicker);
                        setShowStatusPicker(false);
                      }}
                      className="flex-row items-center justify-between px-3 py-2.5 rounded-xl"
                      style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
                    >
                      <FeedbackPriorityBadge priority={fb.priority} size="md" />
                      <ChevronDown size={14} color={colors.text.tertiary} />
                    </AnimatedPressable>
                    {showPriorityPicker && (
                      <View
                        className="rounded-xl mt-1 overflow-hidden"
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderWidth: 1,
                          borderColor: colors.border.light,
                        }}
                      >
                        {PRIORITIES.map((p) => (
                          <AnimatedPressable
                            key={p}
                            onPress={() => handlePriorityChange(p)}
                            className="px-3 py-2.5"
                            style={p !== PRIORITIES[PRIORITIES.length - 1] ? {
                              borderBottomWidth: 1,
                              borderBottomColor: colors.border.light,
                            } : undefined}
                          >
                            <FeedbackPriorityBadge priority={p} size="md" />
                          </AnimatedPressable>
                        ))}
                      </View>
                    )}
                  </View>
                </View>
              </WebCard>
            </Animated.View>
          )}

          {/* Admin Notes */}
          {canAddNotes && (
            <Animated.View entering={FadeInDown.delay(300).duration(400)}>
              <WebCard>
                <Text style={{ color: colors.text.primary }} className="text-sm font-bold mb-3">
                  Admin Notes
                </Text>

                {detail.notes.length === 0 ? (
                  <View className="py-3 items-center">
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">
                      No notes yet
                    </Text>
                  </View>
                ) : (
                  <View className="gap-3 mb-4">
                    {detail.notes.map((note) => (
                      <View
                        key={note.id}
                        className="p-3 rounded-xl"
                        style={{ backgroundColor: colors.surface.secondary }}
                      >
                        <View className="flex-row items-center gap-2 mb-2">
                          <View
                            className="w-6 h-6 rounded-full items-center justify-center"
                            style={{ backgroundColor: `${colors.primary[600]}15` }}
                          >
                            <User size={12} color={colors.primary[600]} />
                          </View>
                          <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">
                            {note.admin_email}
                          </Text>
                          <Text style={{ color: colors.text.tertiary }} className="text-[10px]">
                            {formatDate(note.created_at)}
                          </Text>
                        </View>
                        <Text style={{ color: colors.text.primary }} className="text-sm leading-relaxed">
                          {note.text}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Add note input */}
                <View
                  className="flex-row items-end gap-2 rounded-xl p-2"
                  style={{
                    backgroundColor: colors.surface.secondary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                  }}
                >
                  <TextInput
                    style={noOutlineStyle({ color: colors.text.primary })}
                    className="flex-1 py-2 px-2 text-sm"
                    placeholder="Add a note..."
                    placeholderTextColor={colors.text.tertiary}
                    value={noteText}
                    onChangeText={setNoteText}
                    multiline
                    numberOfLines={2}
                  />
                  <AnimatedPressable
                    onPress={handleAddNote}
                    className="w-9 h-9 rounded-xl items-center justify-center"
                    style={{
                      backgroundColor: noteText.trim() ? colors.primary[600] : colors.surface.tertiary,
                    }}
                  >
                    <Send
                      size={16}
                      color={noteText.trim() ? '#FFFFFF' : colors.text.tertiary}
                    />
                  </AnimatedPressable>
                </View>
              </WebCard>
            </Animated.View>
          )}
        </KeyboardAwareBody>
      </SafeAreaView>
    </AppLayout>
  );
}

function MetaRow({
  label,
  value,
  colors,
  icon,
  onPress,
}: {
  label: string;
  value: string;
  colors: ReturnType<typeof useThemeColors>;
  icon?: React.ReactNode;
  onPress?: () => void;
}) {
  const Content = (
    <View className="flex-row items-center justify-between py-1">
      <Text style={{ color: colors.text.tertiary }} className="text-xs font-medium w-20">
        {label}
      </Text>
      <View className="flex-row items-center gap-1.5 flex-1 justify-end">
        {icon}
        <Text
          style={{ color: onPress ? colors.primary[600] : colors.text.primary }}
          className={`text-xs ${onPress ? 'font-semibold' : ''}`}
        >
          {value}
        </Text>
      </View>
    </View>
  );

  if (onPress) {
    return (
      <AnimatedPressable onPress={onPress}>
        {Content}
      </AnimatedPressable>
    );
  }

  return Content;
}
